/**
 * The platform's industry profiles (task 79, decision 0058). Manufacturing is the model's calibration base,
 * so its profile changes nothing in the score; the others move weight to where their losses come from and
 * make the pack control that matters most a kill switch. Multipliers are the model's (v4.2), shown here so
 * the studio can tune them.
 */
export function industryProfiles(journey) {
    return {
        manufacturing: {
            name: 'Manufacturing',
            description: 'Production downtime drives the losses. The model is calibrated on manufacturers, so the weights are the model’s own.', // prettier-ignore
            journey,
            depth: 'standard',
            larger: { staff: 100, depth: 'thorough' },
            multiplier: 1.25,
            minimum_band: 'FAIR',
        },
        healthcare: {
            name: 'Healthcare',
            description: 'Patient records, medical devices, telehealth and clinical downtime. Data protection and recovery weigh more; unencrypted patient records cap the score.', // prettier-ignore
            journey,
            depth: 'thorough',
            multiplier: 1.45,
            domain_weights: { DP: 14, BR: 12 },
            kill_switches: [{ control: 'DP-14', cap: 649, severity: 'high' }],
            // Patient records, backups and reading records in an outage rest on more than the clinic's word before
            // cover is accepted (BR-20, task 83).
            review: { evidence_before_accept: ['DP-14', 'BR-02', 'BR-03', 'BR-20'] },
            minimum_band: 'GOOD',
        },
        retail_ecommerce: {
            name: 'Retail & e-commerce',
            description: 'Card data and the online store. Data protection and patching weigh more; storing card data caps the score.', // prettier-ignore
            journey,
            depth: 'standard',
            larger: { staff: 100, depth: 'thorough' },
            multiplier: 1.2,
            domain_weights: { DP: 12, VM: 11 },
            kill_switches: [{ control: 'DP-16', cap: 599, severity: 'critical' }],
            minimum_band: 'FAIR',
        },
        professional_services: {
            name: 'Professional services',
            description: 'Client money and client files. Email fraud and data protection weigh more; unconfirmed payment instructions cap the score.', // prettier-ignore
            journey,
            depth: 'light',
            larger: { staff: 50, depth: 'standard' },
            multiplier: 1.1,
            domain_weights: { EH: 11, DP: 11 },
            kill_switches: [{ control: 'EH-14', cap: 649, severity: 'high' }],
            minimum_band: 'FAIR',
        },
        logistics: {
            name: 'Logistics',
            description: 'Partners, carriers and keeping the fleet moving. Suppliers, backups and recovery weigh more.', // prettier-ignore
            journey,
            depth: 'standard',
            larger: { staff: 100, depth: 'thorough' },
            multiplier: 1.2,
            domain_weights: { TP: 7, BR: 11, IR: 8 },
            kill_switches: [{ control: 'EH-15', cap: 679, severity: 'high' }],
            minimum_band: 'FAIR',
        },
        financial_services: {
            name: 'Financial services',
            description: 'Regulated (BNM RMiT) and a target for fraud. A BNM-licensed institution is asked RMiT in full; a moneylender or broker the lighter pack. Identity, monitoring, suppliers and governance weigh more; unrecorded privileged access and unmonitored transactions cap the score.', // prettier-ignore
            journey,
            depth: 'thorough',
            multiplier: 1.5,
            domain_weights: { IA: 14, LD: 10, TP: 7, GV: 8 },
            kill_switches: [
                { control: 'IA-20', cap: 599, severity: 'critical' },
                { control: 'LD-13', cap: 649, severity: 'high' },
            ],
            // As RMiT expects: two underwriters, and privileged access and fraud monitoring evidenced; for a licensed
            // institution also its SOC and its four-hour recovery (task 82; a control not asked isn't waited for).
            review: { sign_offs: 2, evidence_before_accept: ['IA-20', 'LD-13', 'LD-16', 'BR-18'] },
            minimum_band: 'GOOD',
        },
        // Task 81. Energy, utilities, telecoms and government are critical infrastructure under the Cyber
        // Security Act 2024: deeper checks and Good as the floor. Education is a light check, deeper for a
        // large school or university.
        energy: {
            name: 'Energy, oil & gas',
            description: 'Plants, pipelines and remote sites run by control systems (IEC 62443). Network separation, recovery and assets weigh more; a control network open to the office caps the score.', // prettier-ignore
            journey,
            depth: 'thorough',
            multiplier: 1.5,
            domain_weights: { NS: 12, BR: 12, IR: 9, AM: 8 },
            kill_switches: [{ control: 'NS-17', cap: 549, severity: 'critical' }],
            review: {
                sign_offs: 2,
                specialist: {
                    label: 'OT specialist',
                    controls: ['NS-17', 'IA-23', 'AM-14', 'BR-17', 'EP-18', 'NS-22', 'TP-17'],
                },
            },
            minimum_band: 'GOOD',
        },
        utilities: {
            name: 'Utilities',
            description: 'Water, power and gas supply, with stations and meters in the field. Networks and recovery weigh more; shared operator logins cap the score.', // prettier-ignore
            journey,
            depth: 'thorough',
            multiplier: 1.4,
            domain_weights: { NS: 11, BR: 11, IR: 9 },
            kill_switches: [{ control: 'IA-24', cap: 649, severity: 'high' }],
            review: { specialist: { label: 'OT specialist', controls: ['NS-18', 'IA-24', 'IR-15'] } },
            minimum_band: 'GOOD',
        },
        telecoms: {
            name: 'Telecommunications',
            description: 'Networks other businesses run on, and the customer accounts fraudsters want. Identity, network, monitoring and suppliers weigh more; unverified SIM swaps cap the score.', // prettier-ignore
            journey,
            depth: 'thorough',
            multiplier: 1.35,
            domain_weights: { IA: 13, NS: 11, LD: 10, TP: 7 },
            kill_switches: [{ control: 'IA-25', cap: 649, severity: 'high' }],
            minimum_band: 'GOOD',
        },
        government: {
            name: 'Government & public sector',
            description: 'Official information and public services. Governance, identity, data and email weigh more; leavers who keep their access cap the score.', // prettier-ignore
            journey,
            depth: 'standard',
            larger: { staff: 100, depth: 'thorough' },
            multiplier: 1.4,
            domain_weights: { GV: 9, IA: 13, DP: 11, EH: 9 },
            kill_switches: [{ control: 'IA-26', cap: 649, severity: 'high' }],
            review: { sign_offs: 2 },
            minimum_band: 'GOOD',
        },
        education: {
            name: 'Education',
            description: 'Student records and many shared devices on a tight budget. Data, email and identity weigh more; student records open to every staff member cap the score.', // prettier-ignore
            journey,
            depth: 'light',
            larger: { staff: 100, depth: 'standard' },
            multiplier: 1.15,
            domain_weights: { DP: 12, EH: 10, IA: 12 },
            kill_switches: [{ control: 'DP-22', cap: 679, severity: 'medium' }],
            minimum_band: 'FAIR',
        },
    };
}
/** Controls a library profile makes a kill switch in its industry. */
export const PROFILE_KILL_SWITCHES = [
    ...new Set(Object.values(industryProfiles('')).flatMap((p) => (p.kill_switches ?? []).map((k) => k.control))),
];
//# sourceMappingURL=profiles.js.map